/**
 * Preference Manager for Tennis Gourmet
 * 
 * Handles user preferences using localStorage (no cookies needed).
 * These preferences don't require cookie consent as they're stored locally only.
 * 
 * Features:
 * - Theme management (light/dark)
 * - Language selection (it/en)
 * - Accessibility settings
 * - Layout preferences
 * - No server transmission (privacy-friendly)
 */

// Supported themes
export type Theme = 'light' | 'dark' | 'auto';

// Supported languages
export type Language = 'it' | 'en';

// Accessibility preferences
export interface AccessibilitySettings {
  fontSize: 'normal' | 'large' | 'extra-large';
  contrast: 'normal' | 'high';
  reducedMotion: boolean;
  screenReader: boolean;
}

// Layout preferences
export interface LayoutSettings {
  menuStyle: 'classic' | 'modern';
  cardLayout: 'grid' | 'list';
  showImages: boolean;
}

// Complete user preferences
export interface UserPreferences {
  theme: Theme;
  language: Language;
  accessibility: AccessibilitySettings;
  layout: LayoutSettings;
  lastUpdated: number;
}

// Storage keys
const STORAGE_KEYS = {
  THEME: 'tg_theme',
  LANGUAGE: 'tg_language',
  ACCESSIBILITY: 'tg_accessibility',
  LAYOUT: 'tg_layout',
  PREFERENCES: 'tg_user_preferences'
} as const;

// Default preferences
const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'auto',
  language: 'it',
  accessibility: {
    fontSize: 'normal',
    contrast: 'normal',
    reducedMotion: false,
    screenReader: false
  },
  layout: {
    menuStyle: 'modern',
    cardLayout: 'grid',
    showImages: true
  },
  lastUpdated: Date.now()
};

class PreferenceManager {
  /**
   * Get current theme preference
   */
  getTheme(): Theme {
    if (typeof window === 'undefined') return DEFAULT_PREFERENCES.theme;
    return (localStorage.getItem(STORAGE_KEYS.THEME) as Theme) || DEFAULT_PREFERENCES.theme;
  }

  /**
   * Set theme preference and apply to document
   */
  setTheme(theme: Theme): void {
    if (typeof window === 'undefined') return;
    
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
    this.applyTheme(theme);
    this.updateLastModified();
    
    // Dispatch event for components to react
    window.dispatchEvent(new CustomEvent('themeChanged', { detail: theme }));
  }

  /**
   * Apply theme to document
   */
  private applyTheme(theme: Theme): void {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    
    if (theme === 'auto') {
      // Use system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', prefersDark);
    } else {
      root.classList.toggle('dark', theme === 'dark');
    }
  }

  /**
   * Get current language preference
   */
  getLanguage(): Language {
    if (typeof window === 'undefined') return DEFAULT_PREFERENCES.language;
    return (localStorage.getItem(STORAGE_KEYS.LANGUAGE) as Language) || DEFAULT_PREFERENCES.language;
  }

  /**
   * Set language preference
   */
  setLanguage(language: Language): void {
    if (typeof window === 'undefined') return;
    
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
    this.updateLastModified();
    
    // Update document language
    document.documentElement.lang = language;
    
    // Dispatch event for components to react
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: language }));
  }

  /**
   * Get accessibility settings
   */
  getAccessibilitySettings(): AccessibilitySettings {
    if (typeof window === 'undefined') return DEFAULT_PREFERENCES.accessibility;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACCESSIBILITY);
      return stored ? JSON.parse(stored) : DEFAULT_PREFERENCES.accessibility;
    } catch {
      return DEFAULT_PREFERENCES.accessibility;
    }
  }

  /**
   * Set accessibility settings
   */
  setAccessibilitySettings(settings: Partial<AccessibilitySettings>): void {
    if (typeof window === 'undefined') return;
    
    const current = this.getAccessibilitySettings();
    const updated = { ...current, ...settings };
    
    localStorage.setItem(STORAGE_KEYS.ACCESSIBILITY, JSON.stringify(updated));
    this.applyAccessibilitySettings(updated);
    this.updateLastModified();
    
    window.dispatchEvent(new CustomEvent('accessibilityChanged', { detail: updated }));
  }

  /**
   * Apply accessibility settings to document
   */
  private applyAccessibilitySettings(settings: AccessibilitySettings): void {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    
    // Font size
    root.classList.remove('text-large', 'text-extra-large');
    if (settings.fontSize === 'large') root.classList.add('text-large');
    if (settings.fontSize === 'extra-large') root.classList.add('text-extra-large');
    
    // Contrast
    root.classList.toggle('high-contrast', settings.contrast === 'high');
    
    // Reduced motion
    root.classList.toggle('reduce-motion', settings.reducedMotion);
    
    // Screen reader
    root.classList.toggle('screen-reader', settings.screenReader);
  }

  /**
   * Get layout settings
   */
  getLayoutSettings(): LayoutSettings {
    if (typeof window === 'undefined') return DEFAULT_PREFERENCES.layout;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.LAYOUT);
      return stored ? JSON.parse(stored) : DEFAULT_PREFERENCES.layout;
    } catch {
      return DEFAULT_PREFERENCES.layout;
    }
  }

  /**
   * Set layout settings
   */
  setLayoutSettings(settings: Partial<LayoutSettings>): void {
    if (typeof window === 'undefined') return;
    
    const current = this.getLayoutSettings();
    const updated = { ...current, ...settings };
    
    localStorage.setItem(STORAGE_KEYS.LAYOUT, JSON.stringify(updated));
    this.updateLastModified();
    
    window.dispatchEvent(new CustomEvent('layoutChanged', { detail: updated }));
  }

  /**
   * Get all user preferences
   */
  getAllPreferences(): UserPreferences {
    return {
      theme: this.getTheme(),
      language: this.getLanguage(),
      accessibility: this.getAccessibilitySettings(),
      layout: this.getLayoutSettings(),
      lastUpdated: this.getLastUpdated()
    };
  }

  /**
   * Set all user preferences
   */
  setAllPreferences(preferences: Partial<UserPreferences>): void {
    if (preferences.theme) this.setTheme(preferences.theme);
    if (preferences.language) this.setLanguage(preferences.language);
    if (preferences.accessibility) this.setAccessibilitySettings(preferences.accessibility);
    if (preferences.layout) this.setLayoutSettings(preferences.layout);
  }

  /**
   * Reset all preferences to defaults
   */
  resetToDefaults(): void {
    if (typeof window === 'undefined') return;
    
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Apply defaults
    this.applyTheme(DEFAULT_PREFERENCES.theme);
    this.applyAccessibilitySettings(DEFAULT_PREFERENCES.accessibility);
    
    window.dispatchEvent(new CustomEvent('preferencesReset'));
  }

  /**
   * Initialize preferences on app start
   */
  initialize(): void {
    if (typeof window === 'undefined') return;
    
    // Apply current preferences
    this.applyTheme(this.getTheme());
    this.applyAccessibilitySettings(this.getAccessibilitySettings());
    
    // Set document language
    document.documentElement.lang = this.getLanguage();
    
    // Listen for system theme changes
    if (this.getTheme() === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', () => {
        if (this.getTheme() === 'auto') {
          this.applyTheme('auto');
        }
      });
    }
  }

  /**
   * Export preferences for backup/sync
   */
  exportPreferences(): string {
    const preferences = this.getAllPreferences();
    return JSON.stringify(preferences, null, 2);
  }

  /**
   * Import preferences from backup
   */
  importPreferences(data: string): boolean {
    try {
      const preferences = JSON.parse(data) as UserPreferences;
      this.setAllPreferences(preferences);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Clear all preference data (for privacy/GDPR)
   */
  clearAllData(): void {
    this.resetToDefaults();
  }

  /**
   * Get last updated timestamp
   */
  private getLastUpdated(): number {
    if (typeof window === 'undefined') return DEFAULT_PREFERENCES.lastUpdated;
    
    const stored = localStorage.getItem('tg_preferences_updated');
    return stored ? parseInt(stored, 10) : DEFAULT_PREFERENCES.lastUpdated;
  }

  /**
   * Update last modified timestamp
   */
  private updateLastModified(): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem('tg_preferences_updated', Date.now().toString());
  }
}

// Export singleton instance
export const preferenceManager = new PreferenceManager();
export default preferenceManager;
